import { Controller } from "@hotwired/stimulus"

// Client for the docked class-chat panel. Realtime delivery is native Turbo
// Streams: the widget mounts a `turbo_stream_from` subscription (see
// _chat_widget.html.erb) that appends/replaces feed nodes directly -- this
// controller no longer talks to ActionCable itself. It stays mounted on the
// widget's data-turbo-permanent root so per-viewer state (mine/other,
// reaction highlight, unread badge, notification sound, autoscroll, mark-read)
// keeps applying to whatever HTML Turbo streams in.
//
// Cross-region contract with the navbar "Чат" button + badge (design D6,
// button and panel live in different DOM regions):
// - listens on `document` for "chat:toggle" (dispatched by the navbar button) and
//   flips its own `openValue`; the button owns its own active-state styling.
// - dispatches `document` "chat:unread" with `{ delta: 1 }` to bump the server-rendered
//   badge by one, or `{ reset: true }` to zero it after mark-read. The server-rendered
//   count is authoritative on every navigation (this client delta is transient).
// - dispatches `document` "chat:state" with `{ open }` whenever openValue changes, so
//   the toggle button can reflect the panel's open/closed state. This is belt-and-
//   suspenders only: because this panel is `data-turbo-permanent`, connect() does not
//   re-fire on a same-class morph, so a freshly rendered button instead self-corrects
//   by reading the live panel DOM on its own connect() (see chat_button_controller.js).
export default class extends Controller {
  static targets = ["panel", "jumpToLatest", "jumpToLatestCount"]

  static values = {
    currentMemberId: Number,
    markReadUrl: String,
    open: { type: Boolean, default: false },
  }

  connect() {
    // Instrumentation counter for tests — incremented once per real connect().
    window.__chatConnectCount = (window.__chatConnectCount || 0) + 1

    // Seed from the server-rendered messages already in the feed, so the
    // first Turbo-streamed append is correctly recognized as genuinely new.
    this.seenMessageIds = new Set(
      Array.from(this.element.querySelectorAll(".chat-message"))
        .map((node) => node.dataset.messageId)
        .filter(Boolean)
    )

    // Guards the one-time `chat:positioned` dispatch that chat_pagination_controller
    // waits on before starting its IntersectionObservers (D8's jump-to-latest state
    // is recomputed from live scroll position on demand -- no stored counter needed).
    this.positioned = false

    // 5.2 (D6): set by handleOpen(), consumed (and cleared) at the top of the
    // very next openValueChanged() call -- see that method's docstring.
    this.systemInitiatedOpen = false

    this.boundHandleToggle = this.handleToggle.bind(this)
    document.addEventListener("chat:toggle", this.boundHandleToggle)

    // 5.2 (D6): desktop auto-open on an incoming call — see video_call_controller.js.
    this.boundHandleOpen = this.handleOpen.bind(this)
    document.addEventListener("chat:open", this.boundHandleOpen)

    this.boundHandleBeforeVisit = this.handleBeforeVisit.bind(this)
    document.addEventListener("turbo:before-visit", this.boundHandleBeforeVisit)

    // Scroll-position preservation across a cross-page Turbo visit: this panel
    // is `data-turbo-permanent`, so Turbo relocates (detaches, then reattaches)
    // this exact node into the newly rendered document instead of re-rendering
    // it -- but a detached scrollable element has its `scrollTop` reset to 0,
    // and reattaching it does not restore that. `turbo:before-render` fires
    // synchronously right before that detach, so it is the last safe moment to
    // read the real (pre-reset) scrollTop. It also fires for a same-class morph
    // refresh, where the node never actually moves -- harmless, since nothing
    // ever consumes the stashed value there (see `restoreFeedScroll`'s
    // docstring). Stashed directly on `this.element` (rather than on `this`)
    // because it must survive into whichever NEW controller instance Stimulus
    // creates for the relocated node -- `this` will be a different object.
    this.boundHandleBeforeRender = this.handleBeforeRender.bind(this)
    document.addEventListener("turbo:before-render", this.boundHandleBeforeRender)

    this.boundHandleFocusChange = this.handleFocusChange.bind(this)
    window.addEventListener("focus", this.boundHandleFocusChange)
    window.addEventListener("blur", this.boundHandleFocusChange)
    document.addEventListener("visibilitychange", this.boundHandleFocusChange)

    // Toolbar/picker open-close is delegated from the panel root so
    // Turbo-streamed appended/replaced message nodes are wired with no extra rewiring.
    this.boundHandleFeedClick = this.handleFeedClick.bind(this)
    this.element.addEventListener("click", this.boundHandleFeedClick)

    // Outside click closes any open reaction picker (attach/emoji popovers
    // already self-close via Ui::Dropdown's own outside-click handling).
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.boundHandleOutsideClick)

    // Turbo Streams append/replace raw HTML into the feed -- this observer is
    // what re-applies per-viewer state (D2) and the new-message side effects
    // (autoscroll/badge/sound) to whatever lands there.
    this.observer = new MutationObserver(this.handleMutations.bind(this))
    const feed = this.feedElement()
    if (feed) this.observer.observe(feed, { childList: true, subtree: true })

    // Jump-to-latest button (D8): reset/hide it once the viewer scrolls back
    // near the bottom themselves.
    this.boundHandleFeedScroll = this.handleFeedScroll.bind(this)
    if (feed) feed.addEventListener("scroll", this.boundHandleFeedScroll)

    this.applyViewerState()

    // Only ever restores something on the connect() that follows a genuine
    // cross-page visit (see the `turbo:before-render` listener above) -- a
    // no-op on this page's very first connect (nothing stashed yet).
    this.restoreFeedScroll()

    // `openValue` is never server-rendered as true -- it is only ever written
    // by this controller onto the permanent node. So an already-open panel at
    // connect() means this is the reconnect after a cross-page visit: the
    // initial `openValueChanged(true, undefined)` returns early and
    // `positionFeedOnOpen` never runs, yet the feed is laid out and was just
    // positioned by `restoreFeedScroll` above. Announce it, or the freshly
    // reconnected chat_pagination_controller would wait forever.
    if (this.openValue) this.announcePositioned()
  }

  disconnect() {
    document.removeEventListener("chat:toggle", this.boundHandleToggle)
    document.removeEventListener("chat:open", this.boundHandleOpen)
    document.removeEventListener("turbo:before-visit", this.boundHandleBeforeVisit)
    document.removeEventListener("turbo:before-render", this.boundHandleBeforeRender)
    window.removeEventListener("focus", this.boundHandleFocusChange)
    window.removeEventListener("blur", this.boundHandleFocusChange)
    document.removeEventListener("visibilitychange", this.boundHandleFocusChange)
    this.element.removeEventListener("click", this.boundHandleFeedClick)
    document.removeEventListener("click", this.boundHandleOutsideClick)

    const feed = this.feedElement()
    if (feed) feed.removeEventListener("scroll", this.boundHandleFeedScroll)

    if (this.observer) this.observer.disconnect()
  }

  // --- Turbo Stream mutation handling (replaces the old ActionCable handlers) ---

  handleMutations(mutations) {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) this.handleAddedNode(node)
      })
    })
  }

  handleAddedNode(node) {
    if (node.matches?.(".chat-message")) {
      this.handleAddedMessage(node)
    } else if (node.matches?.(".chat-reactions")) {
      this.applyReactionHighlight(node)
    }
  }

  // A message_created append and a message_updated/message_deleted replace
  // both land here as an added `.chat-message` node -- `seenMessageIds`
  // distinguishes a genuinely new message (autoscroll + badge/sound) from an
  // update/delete replace (viewer-state only, see D2).
  handleAddedMessage(node) {
    this.applyViewerState(node)

    const messageId = node.dataset.messageId
    if (!messageId || this.seenMessageIds.has(messageId)) return

    this.seenMessageIds.add(messageId)

    const isOwnMessage = Number(node.dataset.senderMemberId) === this.currentMemberIdValue

    // Sticky-bottom (D8): the append itself is unconditional, but the scroll
    // follow only happens when the viewer is already near the bottom or the
    // message is their own send. Otherwise the position is held and the
    // jump-to-latest button surfaces so a member reading history isn't yanked
    // down.
    const feed = this.feedElement()
    if (feed) {
      const emptyState = feed.querySelector('[data-role="chat-empty-state"]')
      if (emptyState) emptyState.remove()

      // Sticky-bottom nearness must reconstruct the PRE-insert distance from
      // bottom -- see isNearBottomBeforeInsert's docstring. `isNearBottom`
      // itself stays reserved for the scroll-driven callers below, which have
      // no just-inserted node to correct for.
      if (isOwnMessage || this.isNearBottomBeforeInsert(feed, node)) this.autoscroll(feed)

      // Realtime pagination contiguity: a Turbo Stream append lands here
      // directly, bypassing chat_pagination_controller's own cursors/
      // observers entirely, which would otherwise desync them from the true
      // DOM-newest node. Must run BEFORE updateJumpToLatest so the jump
      // count below is computed against a DOM that's already consistent
      // (e.g. with an out-of-window node already pulled back out, or a
      // pending-count bump for one that was). `isOwnMessage` is threaded
      // through so the pagination controller can route an own send
      // differently from an incoming message when the window isn't
      // confirmed to be at the true newest -- see
      // chat_pagination_controller.js#handleRealtimeAppend for the full
      // rationale.
      this.notifyPaginationOfRealtimeAppend(feed, node, isOwnMessage)

      this.updateJumpToLatest(feed)
    }

    if (isOwnMessage) return

    // Sender-exclusion gates only the badge/sound and the read-cursor advance —
    // never the append above.
    if (this.isPanelOpenAndFocused()) {
      this.markRead()
    } else if (!this.openValue) {
      this.incrementUnread()
      this.playNotificationSound()
    }
  }

  feedElement() {
    return this.element.querySelector('[data-role="message-feed"]')
  }

  autoscroll(feed) {
    feed.scrollTop = feed.scrollHeight
  }

  isNearBottom(feed) {
    return feed.scrollHeight - feed.scrollTop - feed.clientHeight < 100
  }

  // Append-time nearness check for `handleAddedMessage`'s autoscroll gate.
  // That gate runs from a MutationObserver callback, i.e. AFTER `node` is
  // already spliced into the feed, so `feed.scrollHeight` already includes
  // its rendered height. For a viewer pinned at the bottom, a tall incoming
  // node (image/audio/multi-line) would otherwise make
  // `scrollHeight - scrollTop - clientHeight` read as "not near bottom" even
  // though they were following -- subtract the node's own height back out to
  // reconstruct the distance from bottom as it was just before the insert.
  // NIT: the feed uses `space-y-3` (~12px inter-item top margin -- see
  // chat/panel/component.html.erb) for spacing between messages, which
  // `node.offsetHeight` does not include. Left uncorrected, the
  // reconstructed pre-insert distance reads ~12px too large, so a viewer
  // within ~88-100px of the bottom with a tall incoming node could wrongly
  // read as "not near bottom" and not get autoscrolled. `marginTop` is 0 for
  // a first child (Tailwind's `space-y-*` only applies the sibling
  // combinator), which is exactly correct there too.
  isNearBottomBeforeInsert(feed, node) {
    const marginTop = parseFloat(getComputedStyle(node).marginTop) || 0
    return feed.scrollHeight - node.offsetHeight - marginTop - feed.scrollTop - feed.clientHeight < 100
  }

  // Reaches the pagination controller the same way `scrollToLatest` does --
  // no-op (short feeds / test contexts with no pagination controller
  // mounted) is safe: without pagination there's no cursor/observer state to
  // desync in the first place.
  notifyPaginationOfRealtimeAppend(feed, node, isOwn) {
    const paginationController = this.application.getControllerForElementAndIdentifier(feed, "chat-pagination")
    if (paginationController) paginationController.handleRealtimeAppend(node, { isOwn })
  }

  // --- Jump-to-latest button (D8) ---
  //
  // Position-based, not a running counter: visibility and the badge count are
  // both recomputed from where the feed is actually scrolled to, so scrolling
  // up through history (no new message involved) surfaces the button just as
  // much as a held realtime append does -- "not at the bottom" and "N
  // messages below the viewport" are the same computation (at bottom ⟺ 0
  // below ⟺ hidden), per class-chat-widget-ui/spec.md.

  handleFeedScroll() {
    const feed = this.feedElement()
    if (feed) this.updateJumpToLatest(feed)
  }

  // The loaded window can sit anywhere in the backlog (first-unread anchor,
  // D8), so a plain scroll-to-bottom only reaches the bottom of what's
  // currently in the DOM, not the true newest message. Delegate to the
  // pagination controller, which fetches the newest page fresh when needed
  // (chat_pagination_controller#jumpToLatest) -- fall back to the old
  // same-window scroll when there's no pagination controller to delegate to
  // (short feeds / test contexts).
  async scrollToLatest() {
    const feed = this.feedElement()
    if (!feed) return

    const paginationController = this.application.getControllerForElementAndIdentifier(feed, "chat-pagination")
    if (paginationController) {
      // `jumpToLatest` can no-op without ever touching scrollTop -- on
      // `!response.ok`, a thrown fetch, an empty result, or (even on
      // success) a newest page short enough to already fit the viewport
      // (scrollTop stays 0, so `scrollTop = scrollHeight` below fires no
      // scroll event). Relying solely on that scroll event to hide the
      // button then leaves it on-screen with a stale count. Await the
      // (already-async) call and unconditionally recompute the button state
      // afterwards instead of depending on a scroll event that might not fire.
      await paginationController.jumpToLatest()
      this.updateJumpToLatest(feed)
    } else {
      this.autoscroll(feed)
      this.updateJumpToLatest(feed)
    }
  }

  updateJumpToLatest(feed) {
    if (!this.hasJumpToLatestTarget) return

    const count = this.countMessagesBelowViewport(feed)
    const visible = count > 0
    this.jumpToLatestTarget.classList.toggle("hidden", !visible)
    if (this.hasJumpToLatestCountTarget) {
      this.jumpToLatestCountTarget.textContent = visible ? String(count) : ""
    }
  }

  // A message counts as "below the viewport" if its top edge sits at/past the
  // feed's visible bottom edge. Aligned with `isNearBottom`'s ~100px threshold
  // so a near-bottom position always yields 0 (hidden), matching the "hidden
  // at the bottom" spec requirement exactly.
  //
  // SF2: also folds in chat_pagination_controller's `pendingRealtimeCount` --
  // messages that arrived while the loaded window's contiguity with the true
  // newest wasn't yet confirmed (`afterExhausted` still false) and were
  // therefore pulled back out of the DOM instead of spliced in (see
  // chat_pagination_controller.js#handleRealtimeAppend). Those have no DOM
  // node to derive a below-viewport position from, so without this an
  // open-panel viewer would get zero indication anything arrived, even once
  // scrolled to the bottom of the (incomplete) loaded window -- added
  // unconditionally, including past the near-bottom early return, since
  // "near the bottom of an unconfirmed window" does not mean "at the true
  // bottom" here.
  countMessagesBelowViewport(feed) {
    const paginationController = this.application.getControllerForElementAndIdentifier(feed, "chat-pagination")
    const pendingCount = paginationController?.pendingRealtimeCount || 0

    if (this.isNearBottom(feed)) return pendingCount

    const feedBottom = feed.getBoundingClientRect().bottom
    const domCount = Array.from(feed.querySelectorAll(".chat-message[data-message-id]")).filter(
      (node) => node.getBoundingClientRect().top >= feedBottom
    ).length

    return domCount + pendingCount
  }

  // --- Unread badge (server-authoritative count + client delta, D6) ---

  incrementUnread() {
    document.dispatchEvent(new CustomEvent("chat:unread", { detail: { delta: 1 } }))
  }

  // --- Mark read ---

  async markRead() {
    if (!this.markReadUrlValue) return

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      await fetch(this.markReadUrlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json",
        },
      })
      document.dispatchEvent(new CustomEvent("chat:unread", { detail: { reset: true } }))
    } catch (error) {
      console.error("Failed to mark conversation read:", error)
    }
  }

  isPanelOpenAndFocused() {
    return this.openValue && document.visibilityState === "visible" && document.hasFocus()
  }

  maybeMarkRead() {
    if (this.isPanelOpenAndFocused()) this.markRead()
  }

  // --- Panel open/close (communicated via document CustomEvents; see D6) ---

  handleToggle() {
    this.openValue = !this.openValue
  }

  // 5.2 (D6): system-initiated open (desktop auto-open on incoming call) —
  // opens the panel without the mark-read side effect. A no-op if already open.
  handleOpen() {
    if (this.openValue) return
    this.systemInitiatedOpen = true
    this.openValue = true
  }

  openValueChanged(isOpen, wasOpen) {
    // 5.2 (D6): capture-and-clear up front, regardless of which branch below
    // runs -- so a stray/no-op call while the flag is set (e.g. isOpen false)
    // can never leave it stuck and silently suppress mark-read on the NEXT
    // user-initiated open.
    const systemInitiated = this.systemInitiatedOpen
    this.systemInitiatedOpen = false

    if (this.hasPanelTarget) this.panelTarget.classList.toggle("hidden", !isOpen)
    document.dispatchEvent(new CustomEvent("chat:state", { detail: { open: isOpen } }))
    if (wasOpen === undefined) return // initial value assignment on connect — no side effect
    if (!isOpen) return

    // Open-time scroll anchoring: wait a frame for the panel to actually lay
    // out (it was `hidden` -- `scrollHeight` is 0 while hidden) before
    // positioning the feed. Runs before mark-read so the divider is still
    // there to scroll to regardless of mark-read timing.
    requestAnimationFrame(() => this.positionFeedOnOpen())
    if (!systemInitiated) this.maybeMarkRead()
  }

  positionFeedOnOpen() {
    const feed = this.feedElement()
    if (feed) {
      const anchor = feed.querySelector('[data-role="first-unread-anchor"]')
      if (anchor) {
        anchor.scrollIntoView()
      } else {
        feed.scrollTop = feed.scrollHeight
      }
    }

    this.announcePositioned()
  }

  // Cross-controller contract with chat_pagination_controller: it
  // will not start its IntersectionObservers until the feed is visible and
  // positioned -- guarded so the event only ever dispatches once per
  // connection. `this.positioned` is part of the contract too: the pagination
  // controller connects after this one, so it reads the flag through its
  // `chat` outlet for an announcement made before it was listening.
  announcePositioned() {
    if (this.positioned) return

    this.positioned = true
    document.dispatchEvent(new CustomEvent("chat:positioned"))
  }

  // --- Scroll-position preservation across a cross-page Turbo visit ---
  //
  // See the `turbo:before-render` listener in connect() for why this pair of
  // methods exists and why stashing on `this.element` (not `this`) is required.

  handleBeforeRender() {
    // Closed panel: whatever is stashed here is moot -- reopening always
    // repositions via `positionFeedOnOpen` (anchor or bottom), which runs
    // strictly after this. Skip so a closed, not-yet-laid-out feed never
    // stashes a meaningless `{0, atBottom: true}` in the first place.
    if (!this.openValue) return

    const feed = this.feedElement()
    if (!feed) return

    // Namespaced (not a bare `pendingScrollRestore`) because this expando
    // lives on `this.element`, which is also a `turbo-mount` / video-call
    // host -- see connect()'s docstring for why it has to live there at all.
    this.element.__chatPendingScrollRestore = { scrollTop: feed.scrollTop, atBottom: this.isNearBottom(feed) }
  }

  // Restores to the true bottom rather than the raw stashed offset when the
  // viewer was pinned there, so it still lands correctly even if the feed's
  // height changed while away (e.g. a message arrived mid-navigation) --
  // consistent with the sticky-bottom semantics used elsewhere in this file
  // (see `isNearBottomBeforeInsert`'s docstring).
  //
  // Applies synchronously first, then again on the next frame as a fallback
  // for the panel-was-hidden case (`scrollHeight` is 0 while `hidden`, same
  // caveat as `positionFeedOnOpen`'s) -- re-applying the same captured
  // `state` twice is idempotent (the raw-offset branch writes the same
  // number both times; the atBottom branch re-reads `feed.scrollHeight` live,
  // so it stays correct even if layout only settled between the two calls).
  //
  // The synchronous first apply closes a real race: Turbo Drive renders a
  // cached snapshot preview and then the fresh response as two separate full
  // relocations of this permanent node, each firing its own
  // `turbo:before-render` + connect() pair. With a purely rAF-deferred
  // restore, the preview's restore was still only QUEUED (not yet applied)
  // when the fresh response's `turbo:before-render` ran -- it would read the
  // still-reset `scrollTop === 0`, stash that as the new pending value, and
  // the two queued rAFs would then race to apply last, with the corrupted 0
  // able to win (guaranteed, if the tab was backgrounded in between, since
  // rAF is suspended while hidden). Applying here, synchronously, means the
  // real scrollTop is already correct by the time any subsequent
  // `turbo:before-render` can possibly read it.
  //
  // A same-class morph refresh also fires `turbo:before-render` (stashing a
  // value), but never re-fires connect() to consume it (the node never moves
  // there) -- so it is simply left on `this.element` until either a real
  // cross-page visit's connect() consumes it, or a later `turbo:before-render`
  // overwrites it with a fresher position. Either way the viewer's live scroll
  // position during a morph refresh is never fought over.
  restoreFeedScroll() {
    const state = this.element.__chatPendingScrollRestore
    if (!state) return

    delete this.element.__chatPendingScrollRestore

    const apply = () => {
      const feed = this.feedElement()
      if (!feed) return

      feed.scrollTop = state.atBottom ? feed.scrollHeight : state.scrollTop
    }

    apply()
    requestAnimationFrame(apply)
  }

  handleFocusChange() {
    this.maybeMarkRead()
  }

  handleBeforeVisit() {
    // Best-effort flush: a same-class navigation preserves this permanent node
    // (Turbo keeps the subscription alive), so this only matters for a genuine
    // cross-class visit — ensure the read cursor is current before it tears down.
    this.maybeMarkRead()
  }

  // --- Per-viewer state (D2): mine/other, toolbar gating, own-reaction highlight ---

  applyViewerState(root = this.element) {
    const nodes = root.matches?.(".chat-message") ? [root] : root.querySelectorAll(".chat-message")
    nodes.forEach((node) => this.applyViewerStateToMessage(node))
  }

  applyViewerStateToMessage(node) {
    const senderId = Number(node.dataset.senderMemberId)
    const isMine = senderId === this.currentMemberIdValue

    node.classList.toggle("mine", isMine)
    node.classList.toggle("other", !isMine)

    // Kind-gating (edit = text only) is already server-side (the edit button
    // markup is absent for non-text kinds) — the client only gates ownership.
    const editButton = node.querySelector('[data-role="edit-button"]')
    const deleteButton = node.querySelector('[data-role="delete-button"]')
    if (editButton) editButton.classList.toggle("hidden", !isMine)
    if (deleteButton) deleteButton.classList.toggle("hidden", !isMine)

    this.applyReactionHighlight(node)
  }

  applyReactionHighlight(root) {
    root.querySelectorAll(".chat-reaction-pill[data-member-ids]").forEach((pill) => {
      const memberIds = (pill.dataset.memberIds || "")
        .split(",")
        .filter(Boolean)
        .map(Number)
      pill.classList.toggle("mine", memberIds.includes(this.currentMemberIdValue))
    })
  }

  // --- Toolbar open/close + reaction-picker open/close ---
  //
  // Edit/delete/react are native Turbo now (link_to / button_to / form_with in
  // Chat::Message::Component and Chat::Reactions::Component -- see docstrings
  // there); the endpoints still rely on the existing Turbo Stream broadcasts
  // (message_updated/message_deleted/reaction_changed) to update the DOM. The
  // reaction picker is a Ui::Dropdown (see Chat::Message::Component) with no
  // trigger of its own -- the toolbar's react button disappears the instant
  // its own toolbar closes, so there is no live element for Ui::Dropdown to
  // wire its own toggle/outside-click to. This controller drives the
  // picker's dropdown controller directly (open()/close()) and still owns
  // the toolbar's open/close entirely.

  handleFeedClick(event) {
    const reactButton = event.target.closest('[data-role="react-button"]')
    if (reactButton) return this.openReactionPicker(reactButton.closest(".chat-message"))

    // Falls through last: clicking anywhere else in the bubble (text, meta,
    // avatar area) opens/closes the floating toolbar for that message --
    // but not on interactive descendants (audio play/pause, file-card link,
    // the edit/delete/reaction links and forms, the edit textarea), which
    // must keep their own click behavior undisturbed rather than also
    // toggling the toolbar.
    if (event.target.closest("button, a, input, textarea, .chat-audio-player, .chat-reaction-picker")) return

    const bubble = event.target.closest(".chat-bubble")
    if (bubble) return this.toggleMessageToolbar(bubble.closest(".chat-message"))
  }

  handleOutsideClick(event) {
    if (!event.target.closest('[data-role="react-button"]')) {
      this.element.querySelectorAll(".chat-reaction-picker").forEach((picker) => {
        if (!picker.contains(event.target)) this.dropdownController(picker)?.close()
      })
    }

    this.element.querySelectorAll('[data-role="message-toolbar"].open').forEach((toolbar) => {
      if (!toolbar.contains(event.target) && !event.target.closest(".chat-bubble")) {
        toolbar.classList.remove("open")
      }
    })
  }

  // Opening a toolbar or a reaction picker always closes any other open
  // toolbar/picker first -- the two popovers are mutually exclusive per
  // message and across messages (mockup: clicking a bubble closes any other
  // open menu/reaction-picker).
  closeAllToolbarsAndPickers() {
    this.element.querySelectorAll('[data-role="message-toolbar"].open').forEach((toolbar) => {
      toolbar.classList.remove("open")
    })
    this.element.querySelectorAll(".chat-reaction-picker").forEach((picker) => {
      this.dropdownController(picker)?.close()
    })
  }

  toggleMessageToolbar(message) {
    const toolbar = message?.querySelector('[data-role="message-toolbar"]')
    if (!toolbar) return

    const willOpen = !toolbar.classList.contains("open")
    this.closeAllToolbarsAndPickers()
    if (willOpen) toolbar.classList.add("open")
  }

  openReactionPicker(message) {
    this.closeAllToolbarsAndPickers()
    this.dropdownController(message?.querySelector(".chat-reaction-picker"))?.open()
  }

  dropdownController(element) {
    return element ? this.application.getControllerForElementAndIdentifier(element, "dropdown") : null
  }

  // --- Notification sound (bell-like chime; mirrors video_call_controller.js) ---

  async playNotificationSound() {
    let ctx
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)()
      if (ctx.state === "suspended") {
        await ctx.resume()
      }
      const now = ctx.currentTime
      const masterGain = ctx.createGain()
      masterGain.gain.value = 0.25
      masterGain.connect(ctx.destination)

      // Inharmonic partials + fast attack/exponential decay give a bell-like "ding"
      // instead of a flat beep.
      const partials = [
        { freq: 659.25, gain: 1.0, decay: 0.9 },
        { freq: 1318.51, gain: 0.35, decay: 0.6 },
        { freq: 1975.53, gain: 0.15, decay: 0.4 },
      ]

      partials.forEach(({ freq, gain, decay }) => {
        const osc = ctx.createOscillator()
        const g = ctx.createGain()
        osc.type = "sine"
        osc.frequency.value = freq
        g.gain.setValueAtTime(0, now)
        g.gain.linearRampToValueAtTime(gain, now + 0.01)
        g.gain.exponentialRampToValueAtTime(0.0001, now + decay)
        osc.connect(g)
        g.connect(masterGain)
        osc.start(now)
        osc.stop(now + decay + 0.05)
      })
    } catch (e) {
      // AudioContext may not be available or blocked by browser policy
    } finally {
      if (ctx) setTimeout(() => ctx.close(), 1200)
    }
  }
}
